import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, input } from '@angular/core';
import { AuthService } from '@app/auth/auth.service';
import { AuthConstants } from '@app/common/constants/AuthConstants';
import { TabHeaderComponent } from '@app/shared/components/tab-header/tab-header.component';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { IProjectTab } from '@app/shared/interfaces/iproject-tab';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { addDays } from '@progress/kendo-date-math';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, GridApi } from 'ag-grid-community';
import { ErrorService } from 'app/common/services/error.service';
import { ProjectTypeId } from 'app/shared/data/project-enums';
import { IScheduleActivity } from 'app/shared/interfaces';
import { ScheduleActivity } from '../../../../shared/models/schedule-activity';
import { CustomEndpointService } from '../../../core-data/services/custom-endpoint.service';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { CreateProjectScheduleComponent } from '../create-project-schedule/create-project-schedule.component';
import { FindProjectScheduleComponent } from '../find-project-schedule/find-project-schedule.component';
import { ScheduleTaskGridOptions } from './schedule-task.gridoptions';

export interface IProjectStatus {
  ProjectId: number,
  ProjectStatusId: number,
  ProjectTypeId: number,
  Schedule: { ScheduleId: number }
}

@Component({
  selector: 'app-schedule-task',
  templateUrl: './schedule-task.component.html',
  styleUrls: ['./schedule-task.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    ButtonDirective,
    ButtonLoadingDirective,
    CreateProjectScheduleComponent,
    DialogModule,
    FindProjectScheduleComponent,
    TabHeaderComponent,
    TooltipDirective,
  ],
})
export class ScheduleTaskComponent implements OnInit {
  private auth = inject(AuthService);
  private customEndpointService = inject(CustomEndpointService);
  private lookupPlusService = inject(LookupPlusService);
  private errorService = inject(ErrorService);
  private cdr = inject(ChangeDetectorRef);

  private readOnlyProjectTypes = new Set<ProjectTypeId>(
    [
      ProjectTypeId.DrillOilGasWell,
      ProjectTypeId.CompleteOilGasWell,   //old project type, no longer used for new projects, but still exists for old ones
      ProjectTypeId.CompleteStimOAG,
      ProjectTypeId.CompletePostStimOAG,
      ProjectTypeId.Flowline,
      ProjectTypeId.WellPadFacility,
      ProjectTypeId.WellHookup,
      ProjectTypeId.TankBattery,
      ProjectTypeId.Surface,
      ProjectTypeId.Pipeline
    ]);

  public icons = kendoIcons;
  public scheduleDataLoaded = false;

  private isAllowedToEditSchedule(projectTypeId: ProjectTypeId): boolean {
    return projectTypeId != null && !this.readOnlyProjectTypes.has(projectTypeId);
  }

  public scheduleActivities = new Array<ScheduleActivity>();

  private tProject: string = 'Projects';
  private oProject: IProjectStatus = null;

  private customEndpointPut: string = 'estimatetaskmapping/UpdateMapping';

  readonly projectTab = input<IProjectTab>(undefined);

  public payloadClone = new Array<ScheduleActivity>();

  public isFindScheduleDialogVisible: boolean = false;
  public isCreateScheduleDialogVisible: boolean = false;
  public displaySaveDialog: boolean = false;
  public changedRows: Set<number> = new Set();
  public working: boolean = false;
  public loading: boolean = true;
  public userCanFixSchedule: boolean = false;
  public gridOptions: ScheduleTaskGridOptions;

  private gridApi: GridApi;

  constructor() {
    this.gridOptions = new ScheduleTaskGridOptions();
    this.userCanFixSchedule = this.auth.userHasAnyRole([AuthConstants.ROLES.Admin.name]);//this.auth.userHasScopes('post:fix-project-schedule');
  }

  async ngOnInit() {
    if (await this.getProjectData()) {
      await this.getScheduleActivities();
    }
  }

  async onGridReady(params) {
    this.gridApi = params.api;
  }

  onCellValueChanged(e: CellValueChangedEvent) {
    if (e.oldValue == e.newValue) { return; }
    let rowIndex: number = e.rowIndex;
    let colId = e.column.getColId();

    if (colId == 'ActiveDuration') {
      this.handleDurationValueChange(+e.newValue, rowIndex);

    } else if (colId == 'PlannedStartDate') {
      this.handleStartDateValueChange(e.newValue, rowIndex);
    }

    this.cdr.detectChanges();
    this.gridApi.applyTransaction({ update: [e.data] });
  }

  private async getProjectData(): Promise<boolean> {
    let url: string = `${this.tProject}(${this.projectTab().ProjectId})?$select=ProjectStatusId,ProjectTypeId,ProjectId,Schedule&$expand=Schedule($select=ScheduleId)`;
    let payload: IProjectStatus;

    try {
      payload = await this.lookupPlusService.getSingleTableRow(url);
    }
    catch (error) {
      this.errorService.error("Error Getting Project Status", error);
      return false;
    }

    this.oProject = payload;
    if (this.oProject.Schedule) {
      this.gridOptions.isAllowedToEditSchedule = this.isAllowedToEditSchedule(this.oProject.ProjectTypeId);
      return true;
    }
    return false;
  }

  private clearArray(data: any[], length: number) {
    if (data && data.length) {
      data.length = 0;
      data.length = length;
    } else if (data) {
      data.length = length;
    } else {
      data = [];
      data.length = length;
    }
  }

  private clearArrays(length: number) {
    this.clearArray(this.payloadClone, length);
  }

  private async getScheduleActivities() {

    if (!this.oProject.ProjectTypeId) {
      throw new Error('Project has not yet loaded. Did you properly await the load?')
    }
    let url: string = `estimatetaskmapping/${this.projectTab().ProjectId}`;
    this.payloadClone = [];
    this.changedRows.clear();

    let payload: IScheduleActivity[] | string;

    try {
      payload = await this.lookupPlusService.getLookupNoCount(url);

      if (payload === 'TemplateId Not Found') {
        return;
      }

      let projTypeId = this.oProject.ProjectTypeId;
      let schedules = payload as IScheduleActivity[];

      schedules = this.filterSchedulesByProjectType(projTypeId, schedules);

      this.scheduleActivities = schedules.map(s => new ScheduleActivity(s));

      //temp hack until we change the way we are processing schedules... 
      //see story https://pxd.visualstudio.com/CPPE/_workitems/edit/221809/
      this.scheduleActivities.sort((a, b) => {
        // Make "CONST" phase come first
        const phaseA = a.PhaseStepType.Phase === "CONST" ? 0 : 1;
        const phaseB = b.PhaseStepType.Phase === "CONST" ? 0 : 1;

        if (phaseA !== phaseB) {
          return phaseA - phaseB;
        }

        const sortOrderA = a.PhaseStepType.SortOrder || 0;
        const sortOrderB = b.PhaseStepType.SortOrder || 0;

        return sortOrderA - sortOrderB;
      });

      // populate the multi-selects
      this.clearArrays(this.scheduleActivities.length);

      for (let i = 0; i < this.scheduleActivities.length; i++) {
        let p: ScheduleActivity = this.scheduleActivities[i];
        if (p !== null && p !== undefined) {

          //duration is in HOURS, but the user wants to see DAYS, so convert here
          p.ActiveDuration = p.ActiveDuration / 24.0;

          // make deep copy of each array element
          this.payloadClone[i] = Object.assign({}, p) as ScheduleActivity;
        }
      }

      this.working = false;
      this.loading = false;
      this.scheduleDataLoaded = this.scheduleActivities.length > 0;
      if (!this.gridApi.isDestroyed()) {
        this.gridApi.setGridOption('rowData', this.scheduleActivities);
        this.gridApi.setGridOption('groupTotalRow', "bottom");
        this.gridApi.setGridOption('grandTotalRow', "bottom");
      }

      this.cdr.detectChanges();
    }
    catch (response) {
      const dataErrorMessage = (response.error && response.error.Message) || response.message || response._body;
      this.errorService.error('Error getting Schedule Details', dataErrorMessage);
      this.scheduleDataLoaded = false;
      this.working = false;
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private filterSchedulesByProjectType(projTypeId: number, schedules: IScheduleActivity[]) {
    //TODO: moved filter to API, this should not be necessary

    if (projTypeId === ProjectTypeId.CompleteStimOAG) {
      schedules = schedules.filter(x => x.Phase === 'PRESTIM' || x.Phase === 'STIM' || x.Phase === 'IDLE');
    }
    else if (projTypeId === ProjectTypeId.CompletePostStimOAG) {
      schedules = schedules.filter(x => x.Phase === 'POSTSTIM');
    }
    return schedules;
  }

  private async refreshScheduleAfterCancelOrRebuild() {
    if (await this.getProjectData()) {
      await this.getScheduleActivities();
    }

    this.working = false;
  }

  public toggleFindScheduleDialog() {
    this.isFindScheduleDialogVisible = !this.isFindScheduleDialogVisible;
  }

  public toggleCreateScheduleDialog() {
    this.isCreateScheduleDialogVisible = !this.isCreateScheduleDialogVisible;
  }

  public async submitPayload() {
    let url = `${this.customEndpointPut}?projectId=${this.oProject.ProjectId}`;

    this.cdr.detectChanges();

    let store: ScheduleActivity[] = [];
    this.changedRows.forEach((rowIndex: number) => {
      let item = this.payloadClone[rowIndex];
      let source = this.scheduleActivities[rowIndex];
      item.ActiveDuration = source.ActiveDuration * 24.0;
      item.PlannedDuration = (source.ActiveDuration * 24.0) + ((source.IdleDuration != null && source.IdleDuration != undefined) ? source.IdleDuration : 0);
      item.PlannedStartDate = source.PlannedStartDate;
      item.PlannedEndDate = source.PlannedEndDate;

      store.push(this.payloadClone[rowIndex]);
    });

    if (store.length > 0) {
      try {
        this.displaySaveDialog = true;
        this.working = true;

        await this.customEndpointService.update(url, store);

        await this.refreshScheduleAfterCancelOrRebuild();

        this.displaySaveDialog = false;
        this.cdr.detectChanges();
      }
      catch (error) {
        this.errorService.error('Error Saving Schedule Activities', error);
        this.working = false;
        this.displaySaveDialog = false;
        this.cdr.detectChanges();
      }
    } else {
      await this.refreshScheduleAfterCancelOrRebuild();
      this.cdr.detectChanges();
    }
  }

  public handleStartDateValueChange(value: Date, rowIndex: number): void {
    let data = this.scheduleActivities;
    let one = data[rowIndex];
    const oneDay = 24 * 60 * 60 * 1000;
    let diffDays = Math.round(Math.abs((one.PlannedStartDate.getTime() - value.getTime()) / oneDay));
    one.PlannedStartDate = value;
    one.PlannedEndDate = addDays(one.PlannedStartDate, one.PlannedDuration);
    this.shiftDates(rowIndex + 1, diffDays);
    this.changedRows.add(rowIndex);
  }

  public shiftDates(startingIndex: number, days: number) {
    let data = this.scheduleActivities;
    for (let index = startingIndex; index < data.length; index++) {
      let one = data[index];
      let prior: ScheduleActivity;
      if (index > 0) {
        prior = data[index - 1];
      }
      if (prior.PlannedEndDate && one.PlannedStartDate !== prior.PlannedEndDate) {
        one.PlannedStartDate = prior.PlannedEndDate;
        one.PlannedEndDate = addDays(one.PlannedStartDate, one.PlannedDuration);
      } else {
        one.PlannedStartDate = addDays(one.PlannedStartDate, days);
        if (!one.PlannedEndDate) {
          one.PlannedEndDate = addDays(one.PlannedStartDate, days);
        } else {
          one.PlannedEndDate = addDays(one.PlannedEndDate, days);
        }
      }
      this.changedRows.add(index);
    }
  }

  public get disableSave(): boolean {
    return this.changedRows.size === 0 || this.working;
  }

  public handleDurationValueChange(value: number, rowIndex: number): void {
    let row = this.scheduleActivities[rowIndex];
    let oldPlannedDuration = row.PlannedDuration;
    row.ActiveDuration = value;
    row.PlannedDuration = row.ActiveDuration + ((row.IdleDuration != null && row.IdleDuration != undefined) ? row.IdleDuration : 0);
    row.PlannedEndDate = addDays(row.PlannedStartDate, row.PlannedDuration);
    let daysDiff: number;
    daysDiff = row.PlannedDuration = oldPlannedDuration;
    this.shiftDates(rowIndex + 1, daysDiff);
    this.changedRows.add(rowIndex);
  }

  public handleCancelButton() {
    this.refreshScheduleAfterCancelOrRebuild();
  }
}